import { interviewApi, vacancyApi } from 'api';
import type {
  CreateVacancyDto,
  GenerateVacancySkillsDto,
  UpdateVacancyDto,
  VacancySkills
} from 'api';
import { makeAutoObservable, runInAction } from 'mobx';
import type { Vacancy } from 'types/api';
import { Interview, Media } from 'types/interview';

export type VacancyLevel = 'JUNIOR' | 'MIDDLE' | 'SENIOR';

export enum CandidateStatus {
  INVITED = 'INVITED',
  IN_PROGRESS = 'IN_PROGRESS',
  IN_PROGRESS_INTERVIEW = 'IN_PROGRESS_INTERVIEW',
  IN_PROGRESS_EXTRACT = 'IN_PROGRESS_EXTRACT',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED'
}

export interface Candidate {
  id: string
  vacancyId: string
  email: string
  userId: string
  status: CandidateStatus
  isFavorite: boolean
  createdAt: string
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
  }
}

export type NewVacancy = {
  speciality: string;
  specialityLevel: VacancyLevel;
  position: string;
};

export class VacancyStore {
  newVacancy: NewVacancy = {
    speciality: '',
    specialityLevel: 'JUNIOR',
    position: ''
  };
  vacancy: Vacancy | null = null;

  vacancies: Vacancy[] = [];

  candidates: Candidate[] = [];

  candidateInterview: Interview | null = null;

  candidateReport: Media | null = null;
  
  loading = false;

  error: string | null = null;

  interviews: Interview[] = []

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setNewVacancyField<K extends keyof NewVacancy>(field: K, value: NewVacancy[K]): void {
    this.newVacancy[field] = value;
  }

  async getInterviewsByVacancyId(vacancyId: string): Promise<void> {
    const interviews = await interviewApi.getInterviewsByVacancyId(vacancyId);
    runInAction(() => {
      this.interviews = interviews;
    });
  }

  setNewVacancy(data: Partial<NewVacancy>): void {
    this.newVacancy = { ...this.newVacancy, ...data };
  }

  async getVacancyById(id: string): Promise<void> {
    const vacancy = await vacancyApi.getById(id);
    runInAction(() => {
      this.vacancy = vacancy;
    });
  }

  async loadVacancies(companyId: string): Promise<void> {
    this.loading = true;
    this.error = null;
    try {
      const list = await vacancyApi.getByCompany(companyId);
      runInAction(() => {
        this.vacancies = list;
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : 'Не удалось загрузить вакансии';
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async createVacancy(dto: CreateVacancyDto): Promise<Vacancy> {
    const vacancy = await vacancyApi.create(dto);
    return vacancy;
  }

  /**
   * Подобрать навыки по описательным полям вакансии (id не нужен).
   * Возвращает массив названий навыков для дальнейшего редактирования на клиенте.
   */
  async generateSkills(dto: GenerateVacancySkillsDto): Promise<string[]> {
    return vacancyApi.generateSkills(dto);
  }

  /**
   * Сохранить навыки в вакансию (перезаписывает skills целиком).
   * Доступно только после создания вакансии — нужен её id.
   */
  async saveSkills(id: string, skills: VacancySkills): Promise<Vacancy> {
    const vacancy = await vacancyApi.updateSkills(id, skills);
    runInAction(() => {
      if (this.vacancy?.id === id) {
        this.vacancy = vacancy;
      }
      const idx = this.vacancies.findIndex((v) => v.id === id);
      if (idx !== -1) {
        this.vacancies = this.vacancies.slice(0, idx).concat(vacancy, this.vacancies.slice(idx + 1));
      }
    });
    return vacancy;
  }

  async getCandidates(vacancyId: string): Promise<any[]> {
    const candidates = await vacancyApi.getCandidates(vacancyId);
    runInAction(() => {
      this.candidates = candidates;
    });

    return candidates;
  }

  async updateVacancy(id: string, dto: UpdateVacancyDto): Promise<void> {
    const updated = await vacancyApi.update(id, dto);
    runInAction(() => {
      const idx = this.vacancies.findIndex((v) => v.id === id);
      if (idx !== -1) {
        this.vacancies = this.vacancies.slice(0, idx).concat(updated, this.vacancies.slice(idx + 1));
      }
    });
  }

  async removeVacancy(id: string): Promise<void> {
    await vacancyApi.remove(id);
    runInAction(() => {
      this.vacancies = this.vacancies.filter((v) => v.id !== id);
    });
  }

  clearVacancies(): void {
    this.vacancies = [];
    this.error = null;
  }

  clearError(): void {
    this.error = null;
  }

  async extractEmails(file: File | Blob, vacancyId: string): Promise<{ emails: string[]; errorEmails: string[] }> {
    const res = await vacancyApi.extractEmails(file, vacancyId);

    return res;
  }

  async addCandidates(vacancyId: string, emails: string[]): Promise<void> {
    await vacancyApi.addCandidates(vacancyId, emails);
  }

  async addToFavorites(vacancyId: string, candidateId: string): Promise<void> {
    const updated = await vacancyApi.addToFavorites(vacancyId, candidateId);
    runInAction(() => {
      this.candidates = this.candidates.map((c) =>
        c.id === candidateId ? { ...c, isFavorite: updated.isFavorite } : c
      );
    });
  }

  async removeFromFavorites(vacancyId: string, candidateId: string): Promise<void> {
    const updated = await vacancyApi.removeFromFavorites(vacancyId, candidateId);
    runInAction(() => {
      this.candidates = this.candidates.map((c) =>
        c.id === candidateId ? { ...c, isFavorite: updated.isFavorite } : c
      );
    });
  }

  async resendInvitation(vacancyId: string, email: string): Promise<{ message: string }> {
    return vacancyApi.resendInvitation(vacancyId, email);
  }

  async fetchInviteLink(vacancyId: string, email: string): Promise<{ inviteLink: string, email: string }> {
    return vacancyApi.fetchInviteLink(vacancyId, email);
  }
}

export const vacancyStore = new VacancyStore();
